import type { ProviderKind } from "@cmmd-forge/contracts";
import { memo } from "react";
import { CommandIcon, StarIcon } from "lucide-react";

import { ClaudeAI, CursorIcon, Gemini, type Icon, OpenAI, OpenCodeIcon } from "../Icons";
import type { PickerRailProvider } from "../../cmmd/providerAvailabilityJoin";
import { ComboboxItem } from "../ui/combobox";
import { Kbd } from "../ui/kbd";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";

/**
 * Provider → icon glyph mapping for the model picker rows and sidebar.
 *
 * Forge has one instance per provider kind (no "Codex Personal" sub-instances
 * like t3code), so the mapping is keyed on the rail's provider identity.
 * `gemini` is on it because the rail draws a roadmap row for a provider Forge
 * has not built and therefore has no `ProviderKind` for.
 */
const CommandProviderIcon: Icon = ({ className }) => <CommandIcon className={className} />;

export const PICKER_PROVIDER_ICON_BY_PROVIDER: Record<PickerRailProvider, Icon> = {
  codex: OpenAI,
  claudeAgent: ClaudeAI,
  ollama: OpenAI,
  cmmd: CommandProviderIcon,
  apex: CommandProviderIcon,
  cursor: CursorIcon,
  opencode: OpenCodeIcon,
  gemini: Gemini,
};

/**
 * Provider display labels for the row subtitle. Kept here rather than
 * sourced from session-logic's PROVIDER_OPTIONS so the picker can render a
 * friendlier label than the raw kind ("Claude" vs "claudeAgent").
 */
export const PICKER_PROVIDER_DISPLAY_NAME: Record<PickerRailProvider, string> = {
  codex: "Codex",
  claudeAgent: "Claude",
  ollama: "Ollama",
  cmmd: "CMMD",
  apex: "CMMD",
  cursor: "Cursor",
  opencode: "OpenCode",
  gemini: "Gemini",
};

export const PICKER_PROVIDER_ACCENT_CLASS_BY_PROVIDER: Partial<Record<ProviderKind, string>> = {
  claudeAgent: "text-[#d97757]",
};

export type ModelPickerRowModel = {
  readonly slug: string;
  readonly name: string;
  readonly isDefault?: boolean | undefined;
  readonly isLegacy?: boolean | undefined;
};

export const ModelListRow = memo(function ModelListRow(props: {
  index: number;
  provider: ProviderKind;
  model: ModelPickerRowModel;
  isFavorite: boolean;
  showProvider: boolean;
  showNewBadge?: boolean;
  jumpLabel?: string | null;
  readonly disabled?: boolean;
  readonly disabledReason?: string | undefined;
  onToggleFavorite: () => void;
}) {
  const ProviderIcon = PICKER_PROVIDER_ICON_BY_PROVIDER[props.provider];
  const providerLabel = PICKER_PROVIDER_DISPLAY_NAME[props.provider];

  return (
    <ComboboxItem
      hideIndicator
      index={props.index}
      value={`${props.provider}:${props.model.slug}`}
      disabled={props.disabled ?? false}
      title={props.disabledReason}
      aria-label={
        props.disabledReason ? `${props.model.name}. ${props.disabledReason}` : props.model.name
      }
      className={cn(
        "group min-h-0 w-full cursor-pointer rounded-md border border-transparent px-0 py-0 transition-[background-color,border-color,color]",
        "hover:bg-muted/45 data-highlighted:bg-muted/70 data-selected:border-border/55 data-selected:bg-muted/70 data-selected:text-foreground",
        "[&[data-highlighted][data-selected]]:border-border/60 [&[data-highlighted][data-selected]]:bg-muted/75",
        props.disabled ? "cursor-not-allowed hover:bg-transparent" : null,
      )}
    >
      <div className="flex min-h-touch w-full items-start gap-2 px-3 py-2 md:min-h-13">
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                className="inline-flex size-touch shrink-0 cursor-pointer items-center justify-center text-muted-foreground/75 opacity-70 transition-[color,opacity] group-hover:text-foreground group-hover:opacity-100 md:mt-0.5 md:size-auto"
                onClick={(event) => {
                  event.stopPropagation();
                  props.onToggleFavorite();
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
                type="button"
                aria-label={props.isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <StarIcon
                  className={cn(
                    "size-4",
                    props.isFavorite && "fill-current text-warning-foreground",
                  )}
                />
              </button>
            }
          />
          <TooltipPopup side="top" align="center">
            {props.isFavorite ? "Remove from favorites" : "Add to favorites"}
          </TooltipPopup>
        </Tooltip>

        <div className="min-w-0 flex-1 text-left">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="text-sm font-medium leading-snug md:text-xs flex items-center gap-2 min-w-0 text-foreground">
              <span className="truncate">{props.model.name}</span>
              {props.showNewBadge ? (
                <span
                  className="shrink-0 rounded border border-warning-border bg-warning-surface px-0.5 py-px text-[10px] font-bold uppercase leading-none tracking-wide text-warning-foreground"
                  aria-label="New model"
                >
                  New
                </span>
              ) : null}
            </div>
            {props.jumpLabel ? (
              <Kbd className="h-5 min-w-6 shrink-0 rounded-md bg-muted/80 px-1.5 text-[10px] text-muted-foreground/80">
                {props.jumpLabel}
              </Kbd>
            ) : null}
          </div>
          {props.showProvider && (
            <div className="flex items-center gap-1 mt-0.5">
              {ProviderIcon ? <ProviderIcon className="size-3 shrink-0" /> : null}
              <span className="text-[12px] font-normal leading-snug text-muted-foreground/70 truncate">
                {providerLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </ComboboxItem>
  );
});
